import * as React from "react"

import { cn } from "@/lib/utils"
import { toneDotClass, type StatusTone } from "@/lib/status-colors"

/**
 * A toggle chip for narrowing an operational list.
 *
 * `sm` control size — 40px on mobile, 32px at `md` — because a filter row on a
 * staff surface is thumbed on a phone before it is clicked on a desk.
 */
function FilterChip({
  active,
  tone,
  count,
  className,
  children,
  ...props
}: Omit<React.ComponentPropsWithoutRef<"button">, "type" | "aria-pressed"> & {
  active: boolean
  tone?: StatusTone
  count?: number
}) {
  return (
    <button
      type="button"
      data-slot="filter-chip"
      data-active={active}
      aria-pressed={active}
      className={cn(
        "inline-flex h-10 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-colors md:h-8",
        "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none",
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-input bg-background hover:bg-muted/60",
        className
      )}
      {...props}
    >
      {tone ? (
        <span
          data-slot="filter-chip-dot"
          aria-hidden="true"
          className={
            active
              ? "size-1.5 shrink-0 rounded-full bg-primary-foreground/80"
              : toneDotClass(tone, "size-1.5")
          }
        />
      ) : null}
      {children}
      {count === undefined ? null : (
        <span
          data-slot="filter-chip-count"
          className={cn(
            "tabular-nums",
            active
              ? "text-primary-foreground/70"
              : count === 0
                ? "text-muted-foreground/40"
                : "text-muted-foreground"
          )}
        >
          {count}
        </span>
      )}
    </button>
  )
}

export { FilterChip }
